import React, { useContext, useState } from "react";
import { Button } from "../../components/ui/button";
import { Checkbox, CheckboxField } from "../../components/ui/checkbox";
import { Field, Label, ErrorMessage } from "../../components/ui/fieldset";
import { Heading } from "../../components/ui/heading";
import { Input } from "../../components/ui/input";
import { Strong, Text, TextLink } from "../../components/ui/text";
import { AuthLayout } from "../../components/ui/auth-layout";
import { Link, useNavigate } from "react-router";
import { initiate_login, setup_mfa } from "../../api/AuthApi";
import { Formik, Form, useField } from "formik";
import * as Yup from "yup";
import { GlobalContext } from "../../App";
import { useAuth } from "../../context/AuthContext";
import { getPostLoginPath } from "../../utilities/postLoginRoute";
import logo from "../../assets/logo.png";
// Custom form field component that works with Formik
const FormField = ({ label, ...props }) => {
  const [field, meta] = useField(props);
  const hasError = meta.touched && meta.error;

  return (
    <Field>
      <Label>{label}</Label>
      <Input
        {...field}
        {...props}
        className={hasError ? "border-red-500" : ""}
      />
      {hasError && <ErrorMessage>{meta.error}</ErrorMessage>}
    </Field>
  );
};

const LoginWrapper = () => {
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState("");

  // Validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string().required("Email or username is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoginError("");
      let response = await initiate_login(values.email, values.password);

      if (response.ChallengeName === "SOFTWARE_TOKEN_MFA") {
        navigate("/otp?email=" + values.email + "&session=" + response.Session);
      } else if (response.ChallengeName === "MFA_SETUP") {
        response = await setup_mfa(response.Session);
        navigate(
          "/mfa-setup?SecretCode=" +
            response.SecretCode +
            "&Session=" +
            response.Session
        );
      } else if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        // Redirect to the new password page with the session
        navigate(
          "/new-password?Session=" + response.Session + "&Email=" + values.email
        );
      } else {
        // localStorage.setItem("refresh_token", response.refresh_token);
        authLogin({
          ...response,
          token: response.access_token,
          // isAuthenticated is determined by the presence of a user in the context
        });
        navigate(getPostLoginPath(response.user_type), { replace: true });
      }
    } catch (error) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message || "Login failed. Please try again."
      );
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="grid w-full max-w-sm grid-cols-1 gap-8">
        <img src={logo} width={300} alt="911 Interpreters Logo" />
        <Heading>Sign in to your account</Heading>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid grid-cols-1 gap-6">
              <FormField
                label="Email or Username"
                type="text"
                name="email"
                placeholder="you@example.com"
              />

              <FormField
                label="Password"
                type="password"
                name="password"
                placeholder="••••••••••••"
              />

              {loginError && (
                <div className="text-red-600 text-sm">{loginError}</div>
              )}

              <div className="flex items-center justify-between">
                <TextLink href="/forgot-password" className="text-sm">
                  Forgot password?
                </TextLink>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Signing in..." : "Sign in"}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
};

export default LoginWrapper;
